/**
 * Lời gọi API thông báo (hợp đồng A0 hồ sơ – thông báo, mục 4): thông báo của chính
 * người dùng, số chưa đọc, đánh dấu đã đọc, và gửi thông báo từ tỉnh xuống xã
 * (`notification.send`). Các đường dẫn CHƯA có trong `@htttn/contracts` nên dùng
 * `goi()`/`chuoiTruyVan()` dùng chung như `quan-tri-api.ts`.
 */
import { goi, chuoiTruyVan, LoiApi } from './goi-chung';
import type { TrangDuLieu } from './quan-tri-api';

export { LoiApi };

/** Loại thông báo — khớp CHECK của bảng `thong_bao` (migration 055). */
export const CAC_LOAI_THONG_BAO = [
  'ban_tin_cho_duyet',
  'ban_tin_da_duyet',
  'ban_tin_tra_lai',
  'ban_tin_bi_huy',
  'ban_tin_chia_se',
  'thiet_bi_mat_ket_noi',
  'thiet_bi_ket_noi_lai',
  'lich_phat_loi',
  'xuat_du_lieu_xong',
  'tai_khoan_dat_lai_mk',
  'tai_khoan_bi_khoa',
  'thong_bao_tinh',
] as const;

export type LoaiThongBao = (typeof CAC_LOAI_THONG_BAO)[number];

export type MucDoThongBao = 'thong_thuong' | 'quan_trong' | 'khan';
export const CAC_MUC_DO: readonly MucDoThongBao[] = ['thong_thuong', 'quan_trong', 'khan'];

export interface DongThongBao {
  id: string;
  /** Chuỗi để không vỡ khi máy chủ thêm loại mới. */
  loai: string;
  tieuDe: string;
  noiDung: string | null;
  /** Đường dẫn CMS, vd `/ban-tin/<id>/sua`; null = không điều hướng. */
  duongDan: string | null;
  /** null = hệ thống gửi. */
  nguoiGui: { id: string; ten: string } | null;
  mucDo: MucDoThongBao | null;
  daDoc: boolean;
  createdAt: string;
}

export interface TuyChonDanhSachThongBao {
  page?: number;
  pageSize?: number;
  /** true = chỉ chưa đọc. */
  chuaDoc?: boolean;
  loai?: string;
  /** Ngày `yyyy-mm-dd` (giờ Việt Nam), tính cả ngày. */
  tuNgay?: string;
  denNgay?: string;
}

export interface ThanGuiThongBao {
  tieuDe: string;
  noiDung: string;
  mucDo: MucDoThongBao;
  /** Mã xã, hoặc mã tỉnh = mọi xã. */
  diaBan: string[];
}

export interface DongThongBaoDaGui {
  id: string;
  tieuDe: string;
  mucDo: MucDoThongBao;
  diaBan: string[];
  tenDiaBan: string[];
  soNguoiNhan: number;
  soDaDoc: number;
  nguoiGui: { id: string; ten: string } | null;
  createdAt: string;
}

export const thongBaoApi = {
  danhSach(tc: TuyChonDanhSachThongBao = {}, signal?: AbortSignal): Promise<TrangDuLieu<DongThongBao>> {
    return goi(
      `/api/v1/notifications${chuoiTruyVan({
        page: tc.page,
        pageSize: tc.pageSize,
        chuaDoc: tc.chuaDoc ? 'true' : undefined,
        loai: tc.loai,
        tuNgay: tc.tuNgay,
        denNgay: tc.denNgay,
      })}`,
      { signal },
    );
  },
  soChuaDoc(signal?: AbortSignal): Promise<{ soChuaDoc: number }> {
    return goi('/api/v1/notifications/unread-count', { signal });
  },
  danhDauDaDoc(id: string): Promise<{ soDaDanhDau: number }> {
    return goi(`/api/v1/notifications/${encodeURIComponent(id)}/read`, { phuongThuc: 'POST' });
  },
  danhDauTatCaDaDoc(): Promise<{ soDaDanhDau: number }> {
    return goi('/api/v1/notifications/read-all', { phuongThuc: 'POST' });
  },
  guiThongBao(than: ThanGuiThongBao): Promise<{ id: string; soNguoiNhan: number }> {
    return goi('/api/v1/notifications/broadcasts', { phuongThuc: 'POST', than });
  },
  lichSuGui(
    tc: { page?: number; pageSize?: number } = {},
    signal?: AbortSignal,
  ): Promise<TrangDuLieu<DongThongBaoDaGui>> {
    return goi(`/api/v1/notifications/broadcasts${chuoiTruyVan({ ...tc })}`, { signal });
  },
};

export type ApiThongBao = typeof thongBaoApi;
